import React, { useContext, useMemo, useState } from 'react';

import { Dropdown } from 'antd';
import Axios from 'axios';

import { useLayoutContext } from '@contexts/LayoutContext';

import ModelsSelectorWrapper from '@components/ContactsSelector/ModelsSelector/ModelsSelectorWrapper';
import { NwButton, NwLinkButton } from '@components/Gui/NwButton';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwDrawer from '@components/Gui/NwDrawer';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwIcon from '@components/Gui/NwIcon';
import NewGroupModal from '@components/PackageDrawer/PackageGroupModals/NewGroupModal';

import { usePackageContext } from './PackageContext';
import PackageGroup from './PackageGroup';
import GroupModal from './PackageGroupModals/GroupModal';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const PackageContent = () => {
    const [addModels, setAddModels] = useState(false);
    const [packageGroup, setPackageGroup] = useState('');
    const [openNewGroupModal, setOpenNewGroupModal] = useState(false);
    const [isOpenGroupModal, setIsOpenGroupModal] = useState(false);
    const [isSort, setIsSort] = useState(false);
    const [selectedModels, setSelectedModels] = useState([]);

    const packageContextValue = usePackageContext();
    const { groupedPackage, afterUpdatePackage } = packageContextValue;

    const { isMobile } = useLayoutContext();

    const packageProjectID = groupedPackage.Project ? groupedPackage.Project.ID : null;

    const hasGroups =
        groupedPackage.GroupNames.length > 1 ||
        (groupedPackage.GroupNames.length === 1 && groupedPackage.GroupNames[0] !== '');

    const handleOpenGroupModal = () => {
        setIsOpenGroupModal(true);
    };

    const handleCloseGroupModal = () => {
        setIsOpenGroupModal(false);
    };

    const getAddModelOptions = useMemo(() => {
        const actions = [
            {
                name: 'Add models',
                // Niente icona su mobile: il pulsante pinned resta compatto sulla stessa riga.
                icon: isMobile ? undefined : byPrefixAndName.fass['person-circle-plus'],
                key: 'add-nogroup',
                pinned: true,
                action: () => {
                    handleAddModelsDrawer('');
                },
            },
        ];
        if (groupedPackage.GroupNames.length > 0) {
            groupedPackage.GroupNames.forEach((group) => {
                if (group) {
                    actions.push({
                        name: `Add models to ${group}`,
                        key: `add-${group}`,
                        pinned: false,
                        action: () => {
                            handleAddModelsDrawer(group);
                        },
                    });
                }
            });
        }
        actions.push({
            name: 'Add models to a new group',
            key: 'add-newgroup',
            pinned: false,
            action: () => {
                handleOpenNewGroupModal();
            },
        });
        return actions;
    }, [groupedPackage.GroupNames, isMobile]);

    const getAddModelsMenuProps = () => {
        // La chiave dei gruppi e' 'add-g<indice>' e non contiene il nome: i nomi possono avere
        // trattini o coincidere con le voci speciali 'nogroup' e 'newgroup'.
        const onItemClick = (e) => {
            switch (e.key) {
                case 'add-nogroup':
                    handleAddModelsDrawer('');
                    break;
                case 'add-newgroup':
                    handleOpenNewGroupModal();
                    break;
                default:
                    handleAddModelsDrawer(groupedPackage.GroupNames[Number(e.key.replace('add-g', ''))]);
                    break;
            }
        };
        const items = [];
        if (groupedPackage.GroupNames.length > 0) {
            groupedPackage.GroupNames.forEach((group, index) => {
                if (group) {
                    items.push({
                        label: (
                            <span>
                                ADD MODELS TO <strong>{group.toUpperCase()}</strong>
                            </span>
                        ),
                        key: `add-g${index}`,
                        icon: <NwIcon icon={byPrefixAndName.fasl['people-group']} />,
                    });
                }
            });
        }
        items.push({
            label: (
                <span>
                    ADD MODELS TO <strong>A NEW GROUP</strong>
                </span>
            ),
            key: 'add-newgroup',
            icon: <NwIcon icon={byPrefixAndName.fasl['square-dashed-circle-plus']} />,
        });
        items.push({
            label: (
                <span>
                    ADD <strong>UNGROUPED</strong> MODELS
                </span>
            ),
            key: `add-nogroup`,
            icon: <NwIcon icon={byPrefixAndName.fasl['user-plus']} />,
        });
        return { onClick: onItemClick, items: items };
    };

    const handleOpenNewGroupModal = () => {
        setPackageGroup('');
        setOpenNewGroupModal(true);
    };

    const handleAddModelsDrawer = (pg) => {
        //onUpdatePackage()
        setPackageGroup(pg);
        setAddModels(true);
    };

    const handleCloseNewGroupModal = (groupName) => {
        setOpenNewGroupModal(false);
        if (groupName) {
            setPackageGroup(groupName);
            setAddModels(true);
        }
    };

    const handleCloseAddModels = () => {
        afterUpdatePackage();
        setAddModels(false);
    };

    const removeModels = async () => {
        const querystring = [...selectedModels].map((id) => `ModelIDs=${id}`).join('&');
        await Axios.delete(`/packagemodels/delete?${querystring}`);
        setSelectedModels([]);
        afterUpdatePackage();
    };

    const SelectedModelActions = () => {
        return (
            <div className="selected-models-actions">
                <div>
                    <small>{selectedModels.length} selected models</small>
                    <NwLinkButton size="small" label="clear selection" onClick={() => setSelectedModels([])} />
                </div>
                <NwButton
                    primary
                    icon={byPrefixAndName.fasl['shuffle']}
                    label="change group"
                    onClick={handleOpenGroupModal}
                />
                <NwButton
                    primary
                    icon={byPrefixAndName.fasl['user-xmark']}
                    label="remove models"
                    onClick={removeModels}
                />
            </div>
        );
    };

    return (
        <div className="package-drawer-models-container">
            <NwCard
                icon={byPrefixAndName.fasl['images']}
                title="Package Content"
                padding="0"
                transparent
                extra={
                    <div className="actions-group-holder">
                        {selectedModels && selectedModels.length > 0 ? <SelectedModelActions /> : null}
                        {hasGroups ? (
                            // Trigger esplicito su click: il default di antd e' l'hover, che su touch non parte mai.
                            <Dropdown menu={getAddModelsMenuProps()} trigger={['click']}>
                                <NwButton primary icon={byPrefixAndName.fasl['chevron-down']} label="add models" />
                            </Dropdown>
                        ) : (
                            <NwDropDownMenu primary actions={getAddModelOptions} />
                        )}
                    </div>
                }
            >
                {groupedPackage.GroupedModels.map((group, groupIndex) => (
                    <PackageGroup
                        key={group.groupLabel}
                        groupName={group.groupName}
                        groupIndex={groupIndex}
                        isSort={isSort}
                        selectedModels={selectedModels}
                        setSelectedModels={setSelectedModels}
                    />
                ))}
            </NwCard>
            {openNewGroupModal && <NewGroupModal onClose={handleCloseNewGroupModal} />}
            {isOpenGroupModal && (
                <GroupModal
                    packageModelIDs={[...selectedModels]}
                    show={isOpenGroupModal}
                    onClose={handleCloseGroupModal}
                    clearSelected={() => setSelectedModels([])}
                />
            )}
            {addModels && (
                <NwDrawer
                    id="add-models-drawer"
                    padding="0px"
                    width="80vw"
                    title="Add Models to Package"
                    onClose={handleCloseAddModels}
                >
                    <ModelsSelectorWrapper
                        currentPackage={groupedPackage}
                        defaultView="table"
                        // fixedView
                        isDrawer={true}
                        layouttype="fullpage"
                        itemClickAction="preview-drawer"
                        multipleSelection={false}
                        packageGroup={packageGroup}
                        projectId={packageProjectID}
                        scope="package"
                        startOnProjectModels={packageProjectID ? true : false}
                    />
                </NwDrawer>
            )}
        </div>
    );
};

export default PackageContent;
